import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Gift, createGift, deleteGift, deleteGiftImage, getGifts, toggleGiftPurchased, updateGift, uploadGiftImage } from '../services/api.ts';
import { BirthdayPerson } from './BirthdayList';

// Icons
import { CardGiftcard as GiftIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotesIcon from '@mui/icons-material/Notes';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Tooltip from '@mui/material/Tooltip';

export type GiftIdea = {
  id: string;
  name: string;
  purchased: boolean;
  notes?: string;
  url?: string;
  price?: number;
  birthday_id: string;
  images?: { id: string; image_url: string }[];
};

type GiftDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: BirthdayPerson | null;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    },
  },
});

// URL detection helper function
type TextPart = {
  type: 'text' | 'url';
  content: string;
  href?: string;
};

const renderTextWithLinks = (text: string): TextPart[] => {
  if (!text) return [];
  
  // Improved URL regex pattern that also matches www. prefixed URLs without http/https
  const urlRegex = /(https?:\/\/|www\.)[a-zA-Z0-9()]{1,}\.(?:[a-zA-Z]{2,}|co\.uk|com\.au|co\.jp)([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;
  
  // Split the text into parts (URLs and non-URLs)
  const parts: TextPart[] = [];
  let lastIndex = 0;
  let match;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // Add URL - ensure it has a protocol prefix for href attribute
    const url = match[0];
    const urlWithProtocol = url.startsWith('www.') ? `https://${url}` : url;
    
    parts.push({
      type: 'url',
      content: url,
      href: urlWithProtocol
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  return parts;
};

// Modal image viewer component
const ImageViewer = ({ open, onClose, imageUrl, alt }: { 
  open: boolean; 
  onClose: () => void; 
  imageUrl: string; 
  alt: string;
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop
      }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Fade in={open}>
        <Box sx={{
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          outline: 'none',
        }}>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={imageUrl}
            alt={alt}
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              display: 'block',
              borderRadius: '8px',
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
            }}
          />
        </Box>
      </Fade>
    </Modal>
  );
};

// Inner component that uses React Query hooks
const GiftDrawerContent = ({ open, onOpenChange, person }: GiftDrawerProps) => {
  const [newIdea, setNewIdea] = useState('');
  const [giftNotes, setGiftNotes] = useState<Record<string, string>>({});
  const [unsavedNotes, setUnsavedNotes] = useState<Record<string, boolean>>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingGift, setAddingGift] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<Record<string, boolean>>({});
  const [showImages, setShowImages] = useState<Record<string, boolean>>({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<{ url: string; alt: string } | null>(null);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  
  // Action menu state (used for both mobile and desktop)
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeGiftId, setActiveGiftId] = useState<string | null>(null);
  
  // Use refs to bypass React state for textarea content
  const textareaRefs = useRef<{[key: string]: HTMLTextAreaElement | null}>({});
  
  const queryClient = useQueryClient();
  
  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      setNewIdea('');
      setGiftNotes({});
      setUnsavedNotes({});
      setExpandedId(null);
      setError(null);
      setAddingGift(false);
      setUploadingImage({});
      setShowImages({});
      textareaRefs.current = {};
    }
  }, [open]);

  // Fetch gifts when the drawer is open and we have a person
  const {
    data: giftData,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['gifts', person?.id],
    queryFn: () => getGifts(person?.id as string),
    enabled: !!person?.id && open
  });

  const giftIdeas = giftData || [];
  
  // Sort gift ideas so unpurchased items appear first
  const sortedGiftIdeas = [...giftIdeas].sort((a, b) => {
    if (a.is_purchased === b.is_purchased) return 0;
    return a.is_purchased ? 1 : -1;
  });

  // Create gift mutation
  const createGiftMutation = useMutation({
    mutationFn: (newGift: Pick<Gift, 'birthday_id' | 'name'>) => createGift(newGift),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
      setNewIdea('');
      setAddingGift(false);
    },
    onError: (err: any) => {
      setError('Failed to add gift: ' + (err.message || 'Unknown error'));
      setAddingGift(false);
    }
  });

  // Update gift mutation with debounce
  const updateGiftMutation = useMutation({
    mutationFn: ({ id, gift }: { id: string, gift: { name: string, notes?: string } }) => 
      updateGift(id, gift),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
    },
    onError: (err: any) => {
      setError('Failed to update gift: ' + (err.message || 'Unknown error'));
    }
  });

  // Delete gift mutation
  const deleteGiftMutation = useMutation({
    mutationFn: (id: string) => deleteGift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
    },
    onError: (err: any) => {
      setError('Failed to delete gift: ' + (err.message || 'Unknown error'));
    }
  });

  // Toggle purchase status mutation
  const togglePurchaseMutation = useMutation({
    mutationFn: (id: string) => toggleGiftPurchased(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
    },
    onError: (err: any) => {
      setError('Failed to update purchase status: ' + (err.message || 'Unknown error'));
    }
  });

  // Upload image mutation
  const uploadImageMutation = useMutation({
    mutationFn: ({ giftId, file }: { giftId: string, file: File }) => uploadGiftImage(giftId, file),
    onError: (err: any) => {
      // Only invalidate on error to restore the correct state
      queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
      setError('Failed to upload image: ' + (err.message || 'Unknown error'));
    },
    onSettled: (_, __, variables) => {
      setUploadingImage(prev => ({
        ...prev,
        [variables.giftId]: false
      }));
    }
  });

  // Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: ({ giftId, imageId }: { giftId: string, imageId: string }) => deleteGiftImage(giftId, imageId),
    onError: (err: any) => {
      // Only invalidate on error to restore the correct state
      queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
      setError('Failed to delete image: ' + (err.message || 'Unknown error'));
    }
  });

  // Initialize notes state when gifts are loaded
  useEffect(() => {
    if (giftIdeas.length > 0) {
      const notesMap: Record<string, string> = {};
      giftIdeas.forEach(gift => {
        notesMap[gift.id] = gift.notes || '';
      });
      setGiftNotes(notesMap);
    }
  }, [giftIdeas]);

  // Handle adding a new gift idea
  const handleAddGiftIdea = useCallback(() => {
    if (!person?.id || !newIdea.trim() || addingGift) return;
    
    setAddingGift(true);
    createGiftMutation.mutate({
      birthday_id: person.id,
      name: newIdea.trim()
    });
  }, [person?.id, newIdea, addingGift, createGiftMutation]);

  // Handle key press for add gift input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newIdea.trim() && !addingGift) {
      e.preventDefault();
      handleAddGiftIdea();
    }
  };

  // Toggle purchased status
  const togglePurchased = (id: string) => {
    togglePurchaseMutation.mutate(id);
  };

  // Toggle expanded state for notes
  const toggleExpanded = (id: string) => {
    // If we're closing this note editor, save its content
    if (expandedId === id) {
      const textarea = textareaRefs.current[id];
      if (textarea) {
        const newValue = textarea.value;
        if (newValue !== (giftIdeas.find(g => g.id === id)?.notes || '')) {
          saveNotes(id, newValue);
        }
      }
    }
    setExpandedId(expandedId === id ? null : id);
  };

  // Save notes
  const saveNotes = useCallback((id: string, value?: string) => {
    const notesValue = value !== undefined ? value : 
                       textareaRefs.current[id]?.value || 
                       giftNotes[id] || '';
    
    // Only save if changed
    const currentGift = giftIdeas.find(g => g.id === id);
    if (currentGift && notesValue === currentGift.notes) {
      setExpandedId(null);
      return;
    }

    // Update local state immediately
    setGiftNotes(prev => ({...prev, [id]: notesValue}));
    
    // Update cache directly
    const updatedGiftIdeas = giftIdeas.map(gift => 
      gift.id === id ? { ...gift, notes: notesValue } : gift
    );
    queryClient.setQueryData(['gifts', person?.id], updatedGiftIdeas);
    
    // Close the editor
    setExpandedId(null);
    
    // Fire off API call in background
    if (currentGift) {
      updateGiftMutation.mutate({ 
        id, 
        gift: { name: currentGift.name, notes: notesValue }
      });
    }
  }, [giftIdeas, queryClient, person?.id, updateGiftMutation]);

  // Save all unsaved notes
  const saveAllUnsavedNotes = useCallback(() => {
    // Only need to save if we have an expanded textarea
    if (expandedId) {
      const textarea = textareaRefs.current[expandedId];
      if (textarea) {
        saveNotes(expandedId);
      }
    }
  }, [expandedId, saveNotes]);

  // Save notes when drawer is closed
  useEffect(() => {
    if (!open) {
      saveAllUnsavedNotes();
    }
  }, [open, saveAllUnsavedNotes]);

  // Handle gift deletion
  const handleDeleteGift = (id: string) => {
    if (window.confirm('Are you sure you want to delete this gift idea?')) {
      deleteGiftMutation.mutate(id);
    }
  };

  // Handle image upload
  const handleImageUpload = useCallback((giftId: string, files: FileList | File[]) => {
    if (!files || (files instanceof FileList && files.length === 0) || 
        (Array.isArray(files) && files.length === 0)) return;
    
    // Get the first file, whether it's a FileList or File[]
    const file = files instanceof FileList ? files[0] : files[0];
    
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setUploadingImage(prev => ({
      ...prev,
      [giftId]: true
    }));

    // Create a local URL for immediate display
    const localImageUrl = URL.createObjectURL(file);
    const tempId = `temp-${Date.now()}`;
    
    // Add optimistic image to the UI
    const currentGift = giftIdeas.find(g => g.id === giftId);
    if (currentGift) {
      const optimisticGift = {
        ...currentGift,
        images: [
          ...(currentGift.images || []),
          { id: tempId, image_url: localImageUrl }
        ]
      };
      
      // Update local cache immediately
      const updatedGiftIdeas = giftIdeas.map(gift => 
        gift.id === giftId ? optimisticGift : gift
      );
      queryClient.setQueryData(['gifts', person?.id], updatedGiftIdeas);
      
      // Show images panel if not already shown
      setShowImages(prev => ({
        ...prev,
        [giftId]: true
      }));
    }

    // Perform actual upload in background
    uploadImageMutation.mutate(
      { giftId, file },
      {
        onSuccess: (data) => {
          // Update the temp ID to the real one but keep using local URL
          if (currentGift) {
            const updatedGift = {
              ...currentGift,
              images: [
                ...(currentGift.images?.filter(img => img.id !== tempId) || []),
                { id: data.id, image_url: localImageUrl } // Keep using local URL but with real ID
              ]
            };
            
            const finalGiftIdeas = giftIdeas.map(gift => 
              gift.id === giftId ? updatedGift : gift
            );
            queryClient.setQueryData(['gifts', person?.id], finalGiftIdeas);
          }
        },
        onError: () => {
          // Revert optimistic update on failure
          queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
          setError('Failed to upload image. Please try again.');
        }
      }
    );
  }, [giftIdeas, queryClient, person?.id, uploadImageMutation]);

  // Handle image delete
  const handleDeleteImage = (giftId: string, imageId: string) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      const currentGift = giftIdeas.find(g => g.id === giftId);
      if (!currentGift || !currentGift.images) return;
      
      // Find the image to remove
      const imageToDelete = currentGift.images.find(img => img.id === imageId);
      if (!imageToDelete) return;
      
      // Update optimistically
      const optimisticGift = {
        ...currentGift,
        images: currentGift.images.filter(img => img.id !== imageId)
      };
      
      // Update local cache immediately
      const updatedGiftIdeas = giftIdeas.map(gift => 
        gift.id === giftId ? optimisticGift : gift
      );
      queryClient.setQueryData(['gifts', person?.id], updatedGiftIdeas);
      
      // Handle temp images (not yet uploaded to server)
      if (imageId.startsWith('temp-')) {
        setUploadingImage(prev => ({
          ...prev,
          [giftId]: false
        }));
        return;
      }
      
      // Perform actual deletion in background
      deleteImageMutation.mutate(
        { giftId, imageId },
        {
          onError: () => {
            // Revert optimistic update on failure
            queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
            setError('Failed to delete image. Please try again.');
          }
        }
      );
    }
  };

  // Handle image click to open viewer
  const handleImageClick = (e: React.MouseEvent, imageUrl: string, alt: string) => {
    e.stopPropagation();
    setCurrentImage({ url: imageUrl, alt });
    setViewerOpen(true);
  };

  // Function that opens the action menu (for both mobile and desktop)
  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, giftId: string, customAnchor?: HTMLElement) => {
    event.stopPropagation();
    // If a custom anchor is provided, use that, otherwise use the event target
    const anchorElement = customAnchor || event.currentTarget;
    setActionMenuAnchorEl(anchorElement);
    setActiveGiftId(giftId);
  };

  // Close action menu
  const handleCloseActionMenu = () => {
    setActionMenuAnchorEl(null);
    setActiveGiftId(null);
  };

  // Execute action from menu and close
  const handleActionMenuAction = (callback: () => void) => {
    callback();
    handleCloseActionMenu();
  };

  // Update the file input click handler to work correctly with the action menu
  const handleFileInputClick = (giftId: string) => {
    // Find the file input element
    const fileInput = document.getElementById(`image-upload-${giftId}`);
    if (fileInput) {
      // Need to use setTimeout to allow the menu to close before triggering the file dialog
      // This prevents issues on some mobile browsers
      setTimeout(() => {
        fileInput.click();
      }, 100);
    }
    handleCloseActionMenu();
  };

  // Update the camera capture function to ensure optimistic updates
  const handleCameraCapture = (giftId: string) => {
    // For browsers that support mediaDevices API (modern approach)
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      // Create a temporary video element if it doesn't exist
      let videoElement = document.querySelector('.camera-preview') as HTMLVideoElement;
      
      if (!videoElement) {
        // Create video element for camera preview
        videoElement = document.createElement('video');
        videoElement.className = 'camera-preview';
        videoElement.autoplay = true;
        videoElement.style.position = 'fixed';
        videoElement.style.top = '0';
        videoElement.style.left = '0';
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        videoElement.style.zIndex = '9999';
        document.body.appendChild(videoElement);
        
        // Create capture button
        const captureButton = document.createElement('button');
        captureButton.textContent = 'Take Photo';
        captureButton.style.position = 'fixed';
        captureButton.style.bottom = '20px';
        captureButton.style.left = '50%';
        captureButton.style.transform = 'translateX(-50%)';
        captureButton.style.zIndex = '10000';
        captureButton.style.padding = '12px 24px';
        captureButton.style.backgroundColor = '#1976d2';
        captureButton.style.color = 'white';
        captureButton.style.border = 'none';
        captureButton.style.borderRadius = '24px';
        captureButton.style.fontSize = '16px';
        
        // Create close button
        const closeButton = document.createElement('button');
        closeButton.textContent = '✕';
        closeButton.style.position = 'fixed';
        closeButton.style.top = '20px';
        closeButton.style.right = '20px';
        closeButton.style.zIndex = '10000';
        closeButton.style.width = '40px';
        closeButton.style.height = '40px';
        closeButton.style.backgroundColor = 'rgba(0,0,0,0.5)';
        closeButton.style.color = 'white';
        closeButton.style.border = 'none';
        closeButton.style.borderRadius = '50%';
        closeButton.style.fontSize = '18px';
        
        document.body.appendChild(captureButton);
        document.body.appendChild(closeButton);
        
        // Set up camera stream
        navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment', // Prefer rear camera
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          } 
        })
        .then(stream => {
          videoElement.srcObject = stream;
          
          // Handle capture button click
          captureButton.onclick = () => {
            // Create a canvas to capture the image
            const canvas = document.createElement('canvas');
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;
            const ctx = canvas.getContext('2d');
            
            if (ctx) {
              // Draw video frame to canvas
              ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              
              // Convert to file
              canvas.toBlob(blob => {
                if (blob) {
                  // Create a File object
                  const file = new File([blob], `camera-capture-${new Date().getTime()}.jpg`, { 
                    type: 'image/jpeg',
                    lastModified: new Date().getTime()
                  });
                  
                  // CREATE LOCAL OPTIMISTIC UPDATE DIRECTLY
                  // (This ensures the image displays immediately)
                  const localImageUrl = URL.createObjectURL(blob);
                  const tempId = `temp-${Date.now()}`;
                  
                  // Find the gift and update directly
                  const currentGift = giftIdeas.find(g => g.id === giftId);
                  if (currentGift) {
                    // Create an optimistic update with the new image
                    const optimisticGift = {
                      ...currentGift,
                      images: [
                        ...(currentGift.images || []),
                        { id: tempId, image_url: localImageUrl }
                      ]
                    };
                    
                    // Update local cache immediately
                    const updatedGiftIdeas = giftIdeas.map(gift => 
                      gift.id === giftId ? optimisticGift : gift
                    );
                    
                    // Set directly in the query cache
                    queryClient.setQueryData(['gifts', person?.id], updatedGiftIdeas);
                    
                    // Force show the images panel
                    setShowImages(prev => ({
                      ...prev,
                      [giftId]: true
                    }));
                    
                    // Now actually upload the file
                    uploadImageMutation.mutate(
                      { giftId, file },
                      {
                        onSuccess: (data) => {
                          // Get the latest state
                          const latestGifts = queryClient.getQueryData(['gifts', person?.id]) as any[];
                          const latestGift = latestGifts?.find(g => g.id === giftId);
                          
                          if (latestGift) {
                            // Create the final gift with the server ID but keep the local image URL
                            const finalGift = {
                              ...latestGift,
                              images: [
                                ...(latestGift.images?.filter(img => img.id !== tempId) || []),
                                { id: data.id, image_url: localImageUrl }
                              ]
                            };
                            
                            // Update the query cache again
                            const finalGiftIdeas = latestGifts.map(gift => 
                              gift.id === giftId ? finalGift : gift
                            );
                            queryClient.setQueryData(['gifts', person?.id], finalGiftIdeas);
                          }
                        },
                        onError: (err) => {
                          console.error('Upload failed:', err);
                          // On error, revert optimistic update
                          queryClient.invalidateQueries({ queryKey: ['gifts', person?.id] });
                          setError('Failed to upload image. Please try again.');
                        },
                        onSettled: () => {
                          // Ensure upload indicator is removed
                          setUploadingImage(prev => ({
                            ...prev,
                            [giftId]: false
                          }));
                        }
                      }
                    );
                  }
                  
                  // Clean up camera UI
                  cleanupCamera(stream);
                }
              }, 'image/jpeg', 0.95);
            }
          };
          
          // Handle close button click
          closeButton.onclick = () => {
            cleanupCamera(stream);
          };
        })
        .catch(error => {
          console.error('Camera access error:', error);
          // Fallback to the input method
          const cameraInput = document.getElementById(`camera-input-${giftId}`) as HTMLInputElement;
          if (cameraInput) {
            cameraInput.click();
          }
          
          // Clean up failed attempt
          if (videoElement) document.body.removeChild(videoElement);
          if (document.querySelector('button')) document.body.removeChild(document.querySelector('button')!);
          if (document.querySelectorAll('button')[1]) document.body.removeChild(document.querySelectorAll('button')[1]);
        });
      }
    } else {
      // Fallback to input method for browsers that don't support mediaDevices
      const cameraInput = document.getElementById(`camera-input-${giftId}`) as HTMLInputElement;
      if (cameraInput) {
        cameraInput.click();
      }
    }
    
    // Close action menu if it's open
    if (actionMenuAnchorEl) {
      handleCloseActionMenu();
    }
  };

  // Helper to clean up camera resources
  const cleanupCamera = (stream: MediaStream) => {
    // Stop all tracks
    stream.getTracks().forEach(track => track.stop());
    
    // Remove elements
    const videoElement = document.querySelector('.camera-preview');
    if (videoElement) document.body.removeChild(videoElement);
    
    // Find and remove buttons (capture and close)
    const buttons = document.querySelectorAll('button');
    buttons.forEach(button => {
      if (button.style.zIndex === '10000') {
        document.body.removeChild(button);
      }
    });
  };

  // Handle title edit start
  const handleTitleEditStart = (id: string, currentTitle: string) => {
    setEditingTitleId(id);
    setEditingTitle(currentTitle);
  };

  // Handle title edit save
  const handleTitleEditSave = (id: string) => {
    if (!editingTitle.trim()) {
      // Don't allow empty titles
      setEditingTitle(giftIdeas.find(g => g.id === id)?.name || '');
      setEditingTitleId(null);
      return;
    }

    const currentGift = giftIdeas.find(g => g.id === id);
    if (!currentGift || editingTitle === currentGift.name) {
      // No changes
      setEditingTitleId(null);
      return;
    }

    // Create updated gift object
    const updatedGift = {
      ...currentGift,
      name: editingTitle
    };

    // Update locally first for optimistic UI
    const updatedGiftIdeas = giftIdeas.map(gift => 
      gift.id === id ? updatedGift : gift
    );
    
    // Update the query cache immediately
    queryClient.setQueryData(['gifts', person?.id], updatedGiftIdeas);
    
    // Clear editing state
    setEditingTitleId(null);
    
    // Update on server
    updateGiftMutation.mutate({ 
      id, 
      gift: { name: editingTitle, notes: currentGift.notes || '' }
    }, {
      // On error, revert to original data
      onError: () => {
        const originalGiftIdeas = giftIdeas.map(gift => 
          gift.id === id ? currentGift : gift
        );
        queryClient.setQueryData(['gifts', person?.id], originalGiftIdeas);
        setError('Failed to update gift name. Please try again.');
      }
    });
  };

  // Handle title edit cancel
  const handleTitleEditCancel = () => {
    setEditingTitleId(null);
    setEditingTitle('');
  };

  // Handle title edit key press
  const handleTitleEditKeyPress = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleEditSave(id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleTitleEditCancel();
    }
  };

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={() => {
          saveAllUnsavedNotes();
          onOpenChange(false);
        }}
        keepMounted={false}
        disableEnforceFocus
        disableScrollLock
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 400,
            height: isMobile ? '80vh' : '100%',
            borderTopLeftRadius: isMobile ? 16 : 0,
            borderTopRightRadius: isMobile ? 16 : 0,
            background: 'linear-gradient(145deg, #f8f9fc 0%, #ffffff 100%)',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.15)',
            borderLeft: isMobile ? 'none' : '1px solid rgba(50, 50, 200, 0.05)'
          }
        }}
      >
        <Box sx={{ 
          p: 3,
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative elements */}
          <Box 
            sx={{ 
              position: 'absolute', 
              top: -80, 
              right: -80, 
              width: 220, 
              height: 220, 
              borderRadius: '50%', 
              background: 'radial-gradient(circle, rgba(25, 118, 210, 0.04) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0
            }} 
          />
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: -100, 
              left: -100, 
              width: 270, 
              height: 270, 
              borderRadius: '50%', 
              background: 'radial-gradient(circle, rgba(220, 110, 65, 0.03) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0
            }} 
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="h6" 
                component="h2" 
                sx={{ 
                  color: 'primary.dark',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <GiftIcon color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                Gifts for {person?.name}
              </Typography>
            </Box>
            <IconButton 
              size="small" 
              onClick={() => onOpenChange(false)}
              color="default"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.07)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          <Box sx={{ mb: 3, display: 'flex', gap: 1, position: 'relative', zIndex: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add a gift idea..."
              value={newIdea}
              onChange={(e) => setNewIdea(e.target.value)}
              onKeyPress={handleKeyPress}
              variant="outlined"
              disabled={!person || addingGift}
              inputProps={{
                maxLength: 100,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.07)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 3px 10px rgba(25, 118, 210, 0.15)'
                  }
                }
              }}
            />
            <Button 
              variant="contained" 
              color="primary"
              startIcon={addingGift ? <CircularProgress size={16} /> : <AddIcon />}
              onClick={handleAddGiftIdea}
              disabled={!newIdea || !person || addingGift}
              sx={{
                borderRadius: 2,
                boxShadow: '0 3px 8px rgba(25, 118, 210, 0.2)',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: '0 5px 12px rgba(25, 118, 210, 0.3)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(1px)'
                }
              }}
            >
              {addingGift ? 'Adding...' : 'Add'}
            </Button>
          </Box>
          
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Error loading gift ideas. Please try again.
            </Alert>
          ) : (
            <Box sx={{ 
              flexGrow: 1, 
              overflowY: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              }
            }}>
              <Stack spacing={2}>
                {giftIdeas.length === 0 ? (
                  <Typography align="center" color="text.secondary" sx={{ my: 4 }}>
                    No gift ideas yet. Add some!
                  </Typography>
                ) : (
                  sortedGiftIdeas.map(idea => (
                    <Card 
                      key={idea.id} 
                      variant="outlined"
                      sx={{ 
                        p: 2,
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: 2,
                        background: 'linear-gradient(145deg, #ffffff 0%, #fcfcff 100%)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                        borderColor: idea.is_purchased ? 'rgba(0, 0, 0, 0.1)' : 'rgba(25, 118, 210, 0.15)',
                        opacity: idea.is_purchased ? 0.8 : 1,
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={(e) => {
                        // Only open menu if we're clicking on the card but not on the notes section or other interactive elements
                        if (
                          !e.defaultPrevented && 
                          !(e.target as HTMLElement).closest('.notes-section') &&
                          !(e.target as HTMLElement).closest('button') &&
                          !(e.target as HTMLElement).closest('a') &&
                          editingTitleId !== idea.id
                        ) {
                          // Find the more button in this card to use as anchor for the menu
                          const card = (e.currentTarget as HTMLElement);
                          const moreButton = card.querySelector('.more-button') as HTMLElement;
                          
                          if (moreButton) {
                            handleOpenActionMenu(e, idea.id, moreButton);
                          } else {
                            handleOpenActionMenu(e, idea.id);
                          }
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                          {editingTitleId === idea.id ? (
                            <TextField
                              size="small"
                              autoFocus
                              fullWidth
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => handleTitleEditKeyPress(e, idea.id)}
                              onBlur={() => handleTitleEditSave(idea.id)}
                              inputProps={{
                                maxLength: 100,
                                style: { 
                                  padding: '2px 8px',
                                  fontSize: '1rem'
                                }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'primary.main',
                                  },
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                textDecoration: idea.is_purchased ? 'line-through' : 'none',
                                color: idea.is_purchased ? 'text.disabled' : 'text.primary',
                                cursor: 'pointer',
                                '&:hover': {
                                  color: 'primary.main'
                                },
                                wordBreak: 'break-word',
                                fontWeight: 500
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTitleEditStart(idea.id, idea.name);
                              }}
                            >
                              {idea.name}
                            </Typography>
                          )}
                          {idea.images && idea.images.length > 0 && (
                            <Tooltip title={`${idea.images.length} image${idea.images.length === 1 ? '' : 's'}`} enterDelay={1000}>
                              <PhotoLibraryIcon 
                                fontSize="small" 
                                color="action" 
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setShowImages(prev => ({
                                    ...prev,
                                    [idea.id]: !prev[idea.id]
                                  }));
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        
                        {/* Mobile: More Actions Button */}
                        {isMobile && (
                          <IconButton
                            className="more-button"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenActionMenu(e, idea.id);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                        
                        {/* File input for image upload */}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          id={`image-upload-${idea.id}`}
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              handleImageUpload(idea.id, files);
                            }
                          }}
                        />
                        
                        {/* Camera input for photo capture */}
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          style={{ display: 'none' }}
                          id={`camera-input-${idea.id}`}
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files) {
                              handleImageUpload(idea.id, files);
                            }
                          }}
                        />
                        
                        {/* Desktop: Action Buttons - remove camera button but keep it for mobile */}
                        {!isMobile && (
                          <Box 
                            className="action-buttons"
                            sx={{ 
                              display: 'flex',
                              opacity: 1,
                              visibility: 'visible',
                              transition: 'opacity 0.2s ease, visibility 0.2s ease',
                            }}
                          >
                            <IconButton
                              className="more-button"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenActionMenu(e, idea.id);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      
                      {/* Image Gallery */}
                      <Collapse in={showImages[idea.id] && idea.images && idea.images.length > 0}>
                        <Box sx={{ mt: 1 }}>
                          <ImageList 
                            sx={{ 
                              width: '100%', 
                              maxHeight: 200,
                              m: 0
                            }} 
                            cols={3} 
                            rowHeight={100}
                          >
                            {(idea.images || []).map((image) => (
                              <ImageListItem 
                                key={image.id}
                                sx={{ 
                                  position: 'relative',
                                  '&:hover .delete-button': {
                                    opacity: 1
                                  },
                                  cursor: 'pointer',
                                }}
                              >
                                <img
                                  src={image.image_url}
                                  alt={`Gift: ${idea.name}`}
                                  loading="lazy"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onClick={(e) => handleImageClick(e, image.image_url, `Gift: ${idea.name}`)}
                                />
                                <IconButton
                                  className="delete-button"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteImage(idea.id, image.id);
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    opacity: isMobile ? 0.1 : 0,
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                      bgcolor: 'rgba(0,0,0,0.7)',
                                      opacity: 1
                                    }
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </Box>
                      </Collapse>
                      
                      <Collapse in={expandedId === idea.id}>
                        <Box sx={{ mt: 2 }}>
                          <textarea
                            ref={el => textareaRefs.current[idea.id] = el}
                            placeholder="Add notes about this gift..."
                            defaultValue={idea.notes || ''}
                            style={{
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              resize: 'vertical',
                              outline: 'none',
                            }}
                            disabled={updateGiftMutation.isPending}
                          />
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                            {updateGiftMutation.isPending && expandedId === idea.id && (
                              <Typography variant="caption" color="primary">
                                Saving...
                              </Typography>
                            )}
                            {expandedId === idea.id && !updateGiftMutation.isPending && (
                              <Button 
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={() => saveNotes(idea.id)}
                              >
                                Save Notes
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                      
                      {(giftNotes[idea.id] || idea.notes) && expandedId !== idea.id && (
                        <Box 
                          className="notes-section"
                          sx={{ 
                            pl: 1,
                            pt: 1,
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click handler from firing
                            e.preventDefault();
                            toggleExpanded(idea.id);
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            component="div"
                            sx={{ 
                              fontSize: '0.875rem',
                              fontStyle: 'italic',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {renderTextWithLinks(giftNotes[idea.id] || idea.notes || '').map((part, index) => 
                              part.type === 'url' ? (
                                <Link 
                                  key={index}
                                  href={part.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  underline="always"
                                  sx={{
                                    color: '#5c7db8',
                                    fontStyle: 'italic',
                                    textDecorationColor: 'rgba(92, 125, 184, 0.4)',
                                    textDecorationThickness: '1px',
                                    '&:hover': {
                                      color: '#3f64aa',
                                      textDecorationColor: '#3f64aa',
                                    }
                                  }}
                                >
                                  {part.content}
                                </Link>
                              ) : (
                                <React.Fragment key={index}>{part.content}</React.Fragment>
                              )
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  ))
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </Drawer>
      
      {/* Actions Menu (used for both mobile and desktop) */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleCloseActionMenu}
        PaperProps={{
          elevation: 3,
          sx: { 
            borderRadius: 2, 
            minWidth: 180,
            transition: 'none !important'
          }
        }}
        transitionDuration={0}
        slotProps={{
          backdrop: {
            style: { 
              transition: 'none'
            }
          },
          root: {
            style: {
              transition: 'none'
            }
          }
        }}
        container={document.body}
        disableScrollLock
        disablePortal={false}
      >
        {activeGiftId && (
          <>
            <MenuItem onClick={() => handleActionMenuAction(() => handleFileInputClick(activeGiftId))}>
              <ListItemIcon>
                <AddPhotoAlternateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Add Image from Gallery" />
            </MenuItem>
            
            {isMobile && (
              <MenuItem onClick={() => handleActionMenuAction(() => handleCameraCapture(activeGiftId))}>
                <ListItemIcon>
                  <CameraAltIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Take Photo" />
              </MenuItem>
            )}
            
            <MenuItem onClick={() => handleActionMenuAction(() => toggleExpanded(activeGiftId))}>
              <ListItemIcon>
                <NotesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Edit Notes" />
            </MenuItem>
            
            <MenuItem onClick={() => handleActionMenuAction(() => handleDeleteGift(activeGiftId))}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Delete Gift" sx={{ color: 'error.main' }} />
            </MenuItem>
            
            <MenuItem onClick={() => handleActionMenuAction(() => togglePurchased(activeGiftId))}>
              <ListItemIcon>
                {giftIdeas.find(g => g.id === activeGiftId)?.is_purchased ? 
                   <CheckBoxOutlineBlankIcon fontSize="small" /> : 
                   <CheckBoxIcon fontSize="small" color="primary" />
                }
              </ListItemIcon>
              <ListItemText 
                primary={giftIdeas.find(g => g.id === activeGiftId)?.is_purchased ? 
                  "Mark as Not Purchased" : 
                  "Mark as Purchased"
                } 
              />
            </MenuItem>
          </>
        )}
      </Menu>
      
      {/* Image Viewer Modal */}
      {currentImage && (
        <ImageViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          imageUrl={currentImage.url}
          alt={currentImage.alt}
        />
      )}
    </>
  );
};

// Main component that wraps with QueryClientProvider
const GiftDrawer = (props: GiftDrawerProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <GiftDrawerContent {...props} />
    </QueryClientProvider>
  );
};

export default GiftDrawer; 